<script setup lang="ts">
interface Props {
  recipeId: number
}

const props = defineProps<Props>()

const config = useRuntimeConfig()
const userStore = useUserStore()

const loading = ref(false)
const error = ref(false)
const promos = ref<any[]>([])
const promoModalOpen = ref(false)
const promoDetailsId = ref<number | null>(null)
const showAllPromos = ref(false)

// Validate recipe ID
const isValidRecipeId = computed(() => {
  return props.recipeId && typeof props.recipeId === 'number' && props.recipeId > 0
})

function openPromoDetails(itemId: number) {
  promoDetailsId.value = itemId
  promoModalOpen.value = true
}

async function getPromos() {
  if (!isValidRecipeId.value) {
    console.error('Invalid recipe ID provided to PromoCards:', props.recipeId)
    error.value = true
    return
  }

  try {
    loading.value = true
    error.value = false
    
    const response = await $fetch(`${config.public.apiUrl}/promos/get-recipe-promos`, {
      method: 'GET',
      params: { recipe_id: props.recipeId },
      headers: {
        Authorization: `Bearer ${userStore.access_token}`,
      },
    }) as any

    if (response?.success === 1) {
      promos.value = response.items
    } else {
      console.error('API returned error for recipe promos:', response)
      error.value = true
    }
  }
  catch (err) {
    console.error('Erreur lors de la récupération des promos:', err)
    error.value = true
  }
  finally {
    loading.value = false
  }
}

onMounted(() => {
  if (isValidRecipeId.value) {
    getPromos()
  } else {
    console.error('PromoCards: Invalid recipe ID provided:', props.recipeId)
    error.value = true
  }
})

// Watch for changes in recipe ID
watch(() => props.recipeId, (newId) => {
  if (newId && isValidRecipeId.value) {
    getPromos()
  }
})
</script>

<template>
  <!-- Error state -->
  <div v-if="error" class="text-center p-4">
    <UIcon name="i-mdi-alert-circle" class="text-2xl text-red-400 mb-2" />
    <p class="text-sm text-poivre-500">Impossible de charger les rabais</p>
  </div>

  <!-- Loading state -->
  <div v-else-if="loading" class="text-poivre-500">
    Recherche de rabais en cours...
  </div>

  <!-- Success state with promos -->
  <div v-else-if="promos.length > 0" :class="[showAllPromos ? '' : 'overflow-hidden']">
    <div class="relative">
      <ul class="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4" :class="[showAllPromos ? '' : 'h-60']">
        <FlyerItemCard v-for="item in promos" :key="item.id" :item="item" :recipe-id="recipeId" @open-promo-details="openPromoDetails" />
      </ul>
      <div v-if="!showAllPromos" style="background-image: linear-gradient(to top, rgba(243, 244, 246, 1), rgba(255,0,0,0));" class="absolute h-30 bottom-0 w-full flex items-end justify-center">
        <UButton color="primary" @click="showAllPromos = true">
          Voir tous les rabais ({{ promos.length }})
        </UButton>
      </div>
    </div>
  </div>

  <!-- No promos found -->
  <div v-else class="text-poivre-500">
    Aucun rabais trouvé pour les supermarchés liés à votre profil.
  </div>
</template>
